import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaDownload, FaCode, FaCloud, FaDatabase, FaRocket } from 'react-icons/fa';
import { getExperienceYears } from '../utils/experience';

const Landing: React.FC = () => {
  const experienceYears = getExperienceYears('2021-02');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center pt-24 md:pt-28 overflow-hidden">
      {/* Coding-themed background with image */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
        {/* Background Image with Blur and Parallax */}
        <motion.img 
          src={`${process.env.PUBLIC_URL || ''}/images/coding-background.jpg`}
          alt="Coding background"
          className="absolute inset-0 w-full h-full object-cover opacity-40 dark:opacity-30 blur-sm"
          style={{
            y: scrollY * 0.5,
            scale: 1 + scrollY * 0.0001
          }}
        />
        
        {/* Dark overlay for better text readability */}
        <div className="absolute inset-0 bg-black/40 dark:bg-black/60"></div>
        
        {/* Grid pattern overlay */}
        <div 
          className="absolute inset-0 opacity-5 dark:opacity-3"
          style={{
            backgroundImage: `
              linear-gradient(rgba(59, 130, 246, 0.1) 1px, transparent 1px),
              linear-gradient(90deg, rgba(59, 130, 246, 0.1) 1px, transparent 1px)
            `,
            backgroundSize: '50px 50px'
          }}
        />
        
        {/* Animated gradient orbs */}
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 dark:opacity-15 animate-blob"></div>
        <div className="absolute top-40 right-10 w-72 h-72 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 dark:opacity-15 animate-blob animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 dark:opacity-15 animate-blob animation-delay-4000"></div>
      </div>

      {/* Content */}
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          className="text-center max-w-5xl mx-auto"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          <motion.div
            className="mb-8 flex justify-center"
            variants={itemVariants}
          >
            <motion.div 
              className="relative group"
              whileHover={{ scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              <motion.div 
                className="absolute -inset-4 bg-gradient-to-r from-primary-600 via-indigo-600 to-purple-600 rounded-full blur-lg opacity-75 group-hover:opacity-100 transition duration-1000 group-hover:duration-200"
                animate={{ 
                  scale: [1, 1.1, 1],
                  rotate: [0, 180, 360]
                }}
                transition={{ 
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
              />
              <motion.img
                src="/images/adarsh-chaunal-profile-2021.jpeg"
                alt="Adarsh Chaunal"
                className="relative w-48 h-48 md:w-64 md:h-64 rounded-full object-cover border-4 border-white dark:border-gray-800 shadow-2xl z-10"
                whileHover={{ 
                  rotate: [0, -5, 5, -5, 0],
                  transition: { duration: 0.5 }
                }}
              />
            </motion.div>
          </motion.div>
          
          <motion.h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold text-white mb-6 leading-tight"
            variants={itemVariants}
          >
            <motion.span 
              className="block"
              animate={{ 
                textShadow: [
                  "0 0 0px rgba(59, 130, 246, 0)",
                  "0 0 20px rgba(59, 130, 246, 0.5)",
                  "0 0 0px rgba(59, 130, 246, 0)"
                ]
              }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              Building Intelligent
            </motion.span>
            <motion.span 
              className="block py-5 bg-gradient-to-r from-primary-400 via-purple-400 to-pink-400 bg-clip-text text-transparent"
              animate={{ 
                backgroundPosition: ["0%", "100%", "0%"],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: "linear" }}
            >
              Digital Solutions
            </motion.span>
            <motion.span 
              className="block text-3xl md:text-4xl lg:text-5xl mt-4 text-gray-300 font-normal"
              whileHover={{ scale: 1.05, x: 10 }}
              transition={{ type: "spring", stiffness: 300 }}
            >
              with Code & Innovation
            </motion.span>
          </motion.h1>
          
          <motion.div
            className="flex items-center justify-center gap-2 mb-6"
            variants={itemVariants}
          >
            <div className="h-1 w-12 bg-gradient-to-r from-primary-500 to-indigo-500 rounded-full"></div>
            <span className="text-xl md:text-2xl font-semibold text-white">
              Adarsh Chaunal
            </span>
            <div className="h-1 w-12 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full"></div>
          </motion.div>
          
          <motion.p
            className="text-xl md:text-2xl text-gray-300 mb-4 font-medium"
            variants={itemVariants}
          >
            • Full-Stack Engineer  • Cloud & Systems Design
          </motion.p>
          
          <motion.p
            className="text-lg md:text-xl text-gray-400 mb-8 max-w-3xl mx-auto leading-relaxed"
            variants={itemVariants}
          >
            Crafting scalable enterprise solutions with {experienceYears} years of expertise in .NET, React, Azure Cloud, and AI/ML. 
            Passionate about integrating AI into real-world systems — from automation to smart user experiences — transforming complex business problems into high-performance digital solutions.
          </motion.p>

          {/* Tech highlights */}
          <motion.div
            className="flex flex-wrap items-center justify-center gap-4 mb-10"
            variants={itemVariants}
          >
            {[
              { icon: FaCode, text: "Full-Stack Development", color: "text-primary-400" },
              { icon: FaCloud, text: "Cloud Solutions", color: "text-indigo-400" },
              { icon: FaDatabase, text: "Database Design", color: "text-purple-400" },
              { icon: FaRocket, text: "AI/ML Integration", color: "text-violet-400" },
            ].map((item, index) => {
              const Icon = item.icon;
              return (
                <motion.div
                  key={index}
                  className="flex items-center gap-2 px-4 py-2 bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm rounded-full border border-white/20 cursor-pointer"
                  whileHover={{ 
                    scale: 1.1, 
                    y: -5,
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    borderColor: "rgba(255, 255, 255, 0.4)"
                  }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1, type: "spring", stiffness: 200 }}
                >
                  <motion.div
                    whileHover={{ rotate: 360 }}
                    transition={{ duration: 0.5 }}
                  >
                    <Icon className={item.color} />
                  </motion.div>
                  <span className="text-white text-sm font-medium">{item.text}</span>
                </motion.div>
              );
            })}
          </motion.div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center mb-16"
            variants={itemVariants}
          >
            <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/contact"
                className="group relative px-8 py-4 bg-gradient-to-r from-primary-600 via-indigo-600 to-purple-600 text-white rounded-lg font-semibold hover:from-primary-700 hover:via-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg hover:shadow-xl inline-flex items-center justify-center overflow-hidden"
              >
                <motion.span 
                  className="relative z-10"
                  whileHover={{ x: 5 }}
                >
                  Get In Touch
                </motion.span>
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-primary-400 via-indigo-400 to-purple-400 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 blur-xl"
                  animate={{ 
                    x: [-100, 100],
                  }}
                  transition={{ 
                    duration: 3,
                    repeat: Infinity,
                    repeatType: "reverse"
                  }}
                />
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
              <Link
                to="/projects"
                className="px-8 py-4 bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 dark:hover:bg-gray-700/50 transition-all duration-300 border border-white/20 hover:border-white/40 inline-flex items-center justify-center"
              >
                View My Work
              </Link>
            </motion.div>
            <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
              <a
                href="/resume.pdf"
                download
                className="px-8 py-4 bg-white/10 dark:bg-gray-800/50 backdrop-blur-sm text-white rounded-lg font-semibold hover:bg-white/20 dark:hover:bg-gray-700/50 transition-all duration-300 border border-white/20 hover:border-white/40 inline-flex items-center justify-center"
              >
                <motion.span
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <FaDownload className="mr-2" />
                </motion.span>
                Download Resume
              </a>
            </motion.div>
          </motion.div>

        </motion.div>
      </div>
    </section>
  );
};

export default Landing;
